import type { FeatureFlagValue } from '@posthog/core';
import { PostHog } from 'posthog-js';
import { createContext, useContext } from 'react';

export enum PostHogFeatureFlagMap {
  EnableAdditionalExpressOnboardingOptions = 'additional-express-onboarding-options-enabled',
  EnableAiWorkspaceCustomization = 'ai-workspace-customization',
  EnableAlphaApps = 'enable-alpha-apps',
  EnableAppBuilder = 'enable_app_builder',
  EnableAppBuilderInspector = 'app_builder_inspector',
  EnableAppBuilderStopDuringBuild = 'app-builder-stop-during-build',
  EnableAppBuilderTransfer = 'enable_app_builder_transfer',
  EnableAppBuilderQATab = 'enable_app_builder_qa_tab',
  EnableAppBuilderNewSession = 'enable-app-builder-new-session',
  EnableAppBuilderProductAgentOpenRouter = 'app-builder-product-agent-openrouter',
  EnableAppBuilderPreviewV2 = 'app-builder-preview-v2',
  EnableAppBuilderBreadcrumbEditorCaret = 'app-builder-breadcrumb-editor-caret',
  EnableAppUpdates = 'enable-app-updates',
  EnableAutoModeSelector = 'auto-mode-selector',
  EnableGptModels = 'gpt-models',
  EnableBetaApps = 'enable-beta-apps',
  EnableCompanyImport = 'enable_company_import',
  EnableIUAccessControl = 'iu_access_control',
  EnableMicrodepositVerification = 'enable-microdeposit-verification',
  EnableMessageAppNotification = 'message-app-notification',
  EnableDuplicateFileName = 'duplicate_file_name',
  EnableFilesAppPerformance = 'files-app-performance',
  EnableNewSidebar = 'new-sidebar',
  EnableAuditLogs = 'audit_logs',
  EnableFeatureAppVisibility = 'app_visibility_ocmc_support',
  EnableEventsApiDateFilters = 'events_api_date_filters',
  EnableMcpDashboardUi = 'mcp_dashboard_ui',
  EnableIterateOnProjectGithubActions = 'iterate_on_project_github_actions',
  EnableProductAgent = 'enable_product_agent',
  EnableMultiInstanceApps = 'enable_multi_instance_apps',
  EnableStudioAppApis = 'enable_studio_app_apis',
  EnableStudioConnectors = 'enable_app_integrations',
  EnableStudioSidebar = 'studio-sidebar',
  EnableStudioTemplates = 'studio-templates',
  EnableStudioTemplatesAddAppV2 = 'studio-templates-add-app-v2',
  EnableStudioTemplatesPublish = 'studio-templates-publish',
  EnableStudioOnboarding = 'studioOnboarding',
  EnableStudioFTUX = 'studioFTUX',
  EnableStudioPricing = 'studio-pricing',
  EnableMessagingEnterNewLine = 'messaging_enter_new_line',
  EnableBrowserTraceIds = 'browser-trace-ids',
}

export type FlagContextType = Record<
  'EnableManyCompanies' | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: false,
  EnableAiWorkspaceCustomization: false,
  EnableAlphaApps: false,
  EnableAppBuilder: false,
  EnableAppBuilderInspector: false,
  EnableAppBuilderStopDuringBuild: false,
  EnableAppBuilderTransfer: false,
  EnableAppBuilderQATab: false,
  EnableAppBuilderNewSession: false,
  EnableAppBuilderProductAgentOpenRouter: false,
  EnableAppBuilderPreviewV2: false,
  EnableAppBuilderBreadcrumbEditorCaret: false,
  EnableAppUpdates: false,
  EnableAutoModeSelector: false,
  EnableGptModels: false,
  EnableBetaApps: false,
  EnableCompanyImport: false,
  EnableDuplicateFileName: false,
  EnableFilesAppPerformance: false,
  EnableIUAccessControl: false,
  EnableMessageAppNotification: false,
  EnableMicrodepositVerification: false,
  EnableNewSidebar: false,
  EnableAuditLogs: false,
  EnableEventsApiDateFilters: false,
  EnableFeatureAppVisibility: false,
  EnableMcpDashboardUi: false,
  EnableIterateOnProjectGithubActions: false,
  EnableProductAgent: false,
  EnableMultiInstanceApps: false,
  EnableStudioAppApis: false,
  EnableStudioConnectors: false,
  EnableStudioSidebar: false,
  EnableStudioTemplates: false,
  EnableStudioTemplatesAddAppV2: false,
  EnableStudioTemplatesPublish: false,
  EnableStudioOnboarding: false,
  EnableStudioFTUX: false,
  EnableStudioPricing: false,
  EnableMessagingEnterNewLine: false,
  EnableBrowserTraceIds: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = never;

export const getServerFlagsContextValue = (
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions
    ],
  ),
  EnableAiWorkspaceCustomization: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAiWorkspaceCustomization],
  ),
  EnableAlphaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAlphaApps]),
  EnableAppBuilder: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilder],
  ),
  EnableAppBuilderInspector: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderInspector],
  ),
  EnableAppBuilderStopDuringBuild: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderStopDuringBuild],
  ),
  EnableAppBuilderTransfer: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderTransfer],
  ),
  EnableAppBuilderQATab: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderQATab],
  ),
  EnableAppBuilderNewSession: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderNewSession],
  ),
  EnableAppBuilderProductAgentOpenRouter: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderProductAgentOpenRouter],
  ),
  EnableAppBuilderPreviewV2: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderPreviewV2],
  ),
  EnableAppBuilderBreadcrumbEditorCaret: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderBreadcrumbEditorCaret],
  ),
  EnableAppUpdates: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppUpdates],
  ),
  EnableAutoModeSelector: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAutoModeSelector],
  ),
  EnableGptModels: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableGptModels]),
  EnableBetaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableBetaApps]),
  EnableCompanyImport: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyImport],
  ),
  EnableIUAccessControl: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIUAccessControl],
  ),
  EnableMicrodepositVerification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMicrodepositVerification],
  ),
  EnableMessageAppNotification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMessageAppNotification],
  ),
  EnableDuplicateFileName: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDuplicateFileName],
  ),
  EnableFilesAppPerformance: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFilesAppPerformance],
  ),
  EnableNewSidebar: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewSidebar],
  ),
  EnableAuditLogs: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAuditLogs]),
  EnableFeatureAppVisibility: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFeatureAppVisibility],
  ),
  EnableEventsApiDateFilters: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableEventsApiDateFilters],
  ),
  EnableMcpDashboardUi: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMcpDashboardUi],
  ),
  EnableIterateOnProjectGithubActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions],
  ),
  EnableProductAgent: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableProductAgent],
  ),
  EnableMultiInstanceApps: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMultiInstanceApps],
  ),
  EnableStudioAppApis: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioAppApis],
  ),
  EnableStudioConnectors: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioConnectors],
  ),
  EnableStudioSidebar: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioSidebar],
  ),
  EnableStudioTemplates: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioTemplates],
  ),
  EnableStudioTemplatesAddAppV2: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioTemplatesAddAppV2],
  ),
  EnableStudioTemplatesPublish: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioTemplatesPublish],
  ),
  EnableStudioOnboarding: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioOnboarding],
  ),
  EnableStudioFTUX: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioFTUX],
  ),
  EnableStudioPricing: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioPricing],
  ),
  EnableMessagingEnterNewLine: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMessagingEnterNewLine],
  ),
  EnableBrowserTraceIds: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableBrowserTraceIds],
  ),
});

export const getClientFlagsContextValue = (
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions,
  ),
  EnableAiWorkspaceCustomization: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAiWorkspaceCustomization,
  ),
  EnableAlphaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAlphaApps,
  ),
  EnableAppBuilder: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilder,
  ),
  EnableAppBuilderInspector: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderInspector,
  ),
  EnableAppBuilderStopDuringBuild: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderStopDuringBuild,
  ),
  EnableAppBuilderTransfer: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderTransfer,
  ),
  EnableAppBuilderQATab: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderQATab,
  ),
  EnableAppBuilderNewSession: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderNewSession,
  ),
  EnableAppBuilderProductAgentOpenRouter: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderProductAgentOpenRouter,
  ),
  EnableAppBuilderPreviewV2: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderPreviewV2,
  ),
  EnableAppBuilderBreadcrumbEditorCaret: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderBreadcrumbEditorCaret,
  ),
  EnableAppUpdates: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppUpdates,
  ),
  EnableAutoModeSelector: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAutoModeSelector,
  ),
  EnableGptModels: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableGptModels,
  ),
  EnableBetaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBetaApps,
  ),
  EnableCompanyImport: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyImport,
  ),
  EnableIUAccessControl: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIUAccessControl,
  ),
  EnableMicrodepositVerification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMicrodepositVerification,
  ),
  EnableMessageAppNotification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageAppNotification,
  ),
  EnableDuplicateFileName: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDuplicateFileName,
  ),
  EnableFilesAppPerformance: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFilesAppPerformance,
  ),
  EnableNewSidebar: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewSidebar,
  ),
  EnableAuditLogs: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogs,
  ),
  EnableEventsApiDateFilters: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableEventsApiDateFilters,
  ),
  EnableFeatureAppVisibility: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFeatureAppVisibility,
  ),
  EnableMcpDashboardUi: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMcpDashboardUi,
  ),
  EnableIterateOnProjectGithubActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions,
  ),
  EnableProductAgent: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableProductAgent,
  ),
  EnableMultiInstanceApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMultiInstanceApps,
  ),
  EnableStudioAppApis: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioAppApis,
  ),
  EnableStudioConnectors: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioConnectors,
  ),
  EnableStudioSidebar: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioSidebar,
  ),
  EnableStudioTemplates: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioTemplates,
  ),
  EnableStudioTemplatesAddAppV2: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioTemplatesAddAppV2,
  ),
  EnableStudioTemplatesPublish: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioTemplatesPublish,
  ),
  EnableStudioOnboarding: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioOnboarding,
  ),
  EnableStudioFTUX: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioFTUX,
  ),
  EnableStudioPricing: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioPricing,
  ),
  EnableMessagingEnterNewLine: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessagingEnterNewLine,
  ),
  EnableBrowserTraceIds: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBrowserTraceIds,
  ),
});

export const FlagsContext = createContext(DefaultFlagsContext);

/**
 * Custom hook to access feature flags from the context. This abstracts away the
 * underlying implementation and allows components to easily check if a feature is enabled.
 */
export function useFeatureFlags(): FlagContextType {
  return useContext(FlagsContext);
}
